import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Gauge, Thermometer, Droplet, Wind, Settings } from 'lucide-react'
import { deviceTypeService, DeviceTypeData } from '@/services/deviceType.service'
import { useToast } from '@/components/ui/use-toast'

interface DeviceType {
  id: string
  name: string
  code: string
  description: string
  deviceCount: number
  parameterCount: number
  isActive: boolean
  icon: React.ReactNode
}

// Map device type code to icon and display name
const getDeviceTypeIcon = (code: string) => {
  const lowerCode = code.toLowerCase()
  if (lowerCode.includes('water')) return <Droplet className="h-5 w-5" />
  if (lowerCode.includes('rk900')) return <Gauge className="h-5 w-5" />
  if (lowerCode.includes('dht') || lowerCode.includes('soil')) return <Thermometer className="h-5 w-5" />
  if (lowerCode.includes('wind')) return <Wind className="h-5 w-5" />
  return <Settings className="h-5 w-5" />
}

const getDeviceTypeName = (code: string): string => {
  // Convert code to readable name
  return code.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ')
}

export const DeviceTypeListPage = () => {
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        setLoading(true)
        const data = await deviceTypeService.list()

        // Map API data to component interface
        const mappedData: DeviceType[] = data.map(dt => ({
          id: dt.device_type,
          name: getDeviceTypeName(dt.device_type),
          code: dt.device_type,
          description: dt.description || 'No description available',
          deviceCount: dt.device_count,
          parameterCount: dt.parameter_count,
          isActive: dt.is_active,
          icon: getDeviceTypeIcon(dt.device_type)
        }))

        setDeviceTypes(mappedData)
      } catch (error: any) {
        console.error('Failed to fetch device types:', error)
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to fetch device types',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchDeviceTypes()
  }, [toast])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Device Types</h1>
          <p className="text-muted-foreground">
            Manage device type configurations and parameters
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/device-types/new">
            <Plus className="mr-2 h-4 w-4" />
            New Device Type
          </Link>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-muted-foreground">Loading device types...</div>
        </div>
      ) : deviceTypes.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-muted-foreground">No device types found</div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {deviceTypes.map((deviceType) => (
          <Card key={deviceType.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    {deviceType.icon}
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {deviceType.name}
                      <Badge
                        variant={deviceType.isActive ? 'success' : 'secondary'}
                        className="text-xs"
                      >
                        {deviceType.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {deviceType.description}
                    </CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="icon" asChild>
                  <Link to={`/admin/device-types/${deviceType.id}/edit`}>
                    <Edit className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">Code: </span>
                  <span className="font-mono font-medium">{deviceType.code}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Devices: </span>
                  <span className="font-medium">{deviceType.deviceCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Parameters: </span>
                  <span className="font-medium">{deviceType.parameterCount}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      )}
    </div>
  )
}
